import { claudeCodeInstance } from '../claudeCodeDashboard/instance';
import type {
  McpApiKeySummary,
  McpKeyAdministration,
  McpPersonAccess,
  McpSyncFinding,
  RemovedStaffMember,
  RevokedMcpApiKeys,
  StaffMember,
  StaffMembers,
} from './types';

const KEYS_PATH = '/agent/selfService/apiKey';

export const listOwnMcpApiKeys = async (): Promise<McpApiKeySummary[]> =>
  (await claudeCodeInstance.get<McpApiKeySummary[]>(KEYS_PATH)).data;

export const revokeOwnMcpApiKey = async (keyId: string): Promise<void> => {
  await claudeCodeInstance.delete(`${KEYS_PATH}/${encodeURIComponent(keyId)}`);
};

const PEOPLE_PATH = '/agent/mcpAccess/people';

const personKeysPath = (email: string): string => `${PEOPLE_PATH}/${encodeURIComponent(email)}/apiKeys`;

/** For HR managers and admins: every MCP user, agents included, with their active keys. */
export const listMcpPeople = async (): Promise<McpPersonAccess[]> =>
  (await claudeCodeInstance.get<McpPersonAccess[]>(PEOPLE_PATH)).data;

/** Whether the signed-in person may manage other people's keys, decided by the Facade from their current roles. */
export const getMcpKeyAdministration = async (): Promise<McpKeyAdministration> =>
  (await claudeCodeInstance.get<McpKeyAdministration>('/agent/mcpAccess/administration')).data;

/** For HR managers and admins: revokes one active key of that person. */
export const revokePersonMcpApiKey = async (email: string, keyId: string): Promise<void> => {
  await claudeCodeInstance.delete(`${personKeysPath(email)}/${encodeURIComponent(keyId)}`);
};

/** For HR managers and admins: revokes every active key of that person. */
export const revokeAllPersonMcpApiKeys = async (email: string): Promise<RevokedMcpApiKeys> =>
  (await claudeCodeInstance.delete<RevokedMcpApiKeys>(personKeysPath(email))).data;

const MEMBERS_PATH = '/agent/mcpAccess/members';

const memberPath = (email: string): string => `${MEMBERS_PATH}/${encodeURIComponent(email)}`;

/** For HR managers, admins and team admins: the people whose team roles they may change, and which roles. */
export const listStaffMembers = async (): Promise<StaffMembers> =>
  (await claudeCodeInstance.get<StaffMembers>(MEMBERS_PATH)).data;

/** For HR managers and admins: adds a person to the staff organization with their first team role. */
export const addStaffMember = async (email: string, role: string): Promise<StaffMember> =>
  (await claudeCodeInstance.post<StaffMember>(MEMBERS_PATH, { email, role })).data;

/** For HR managers and admins: removes a person from the staff organization and revokes all their MCP keys. */
export const removeStaffMember = async (email: string): Promise<RemovedStaffMember> =>
  (await claudeCodeInstance.delete<RemovedStaffMember>(memberPath(email))).data;

export const grantStaffRole = async (email: string, role: string): Promise<StaffMember> =>
  (await claudeCodeInstance.post<StaffMember>(`${memberPath(email)}/roles`, { role })).data;

export const removeStaffRole = async (email: string, role: string): Promise<StaffMember> =>
  (await claudeCodeInstance.delete<StaffMember>(`${memberPath(email)}/roles/${encodeURIComponent(role)}`)).data;

/** For HR managers and admins: MCP users whose access does not match Logto. */
export const listMcpSyncFindings = async (): Promise<McpSyncFinding[]> =>
  (await claudeCodeInstance.get<McpSyncFinding[]>('/agent/mcpAccess/sync')).data;

/** For HR managers and admins: sets a person's MCP groups to what their Logto team roles grant now. */
export const syncMcpGroupsWithLogto = async (email: string): Promise<string[]> =>
  (await claudeCodeInstance.post<string[]>(`/agent/mcpAccess/sync/${encodeURIComponent(email)}/groups`)).data;
